import MarkdownIt from "markdown-it";

const markdown = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true,
  typographer: false,
});

export function truncateText(value, maxLength = 180) {
  if (!value) return "";

  const normalized = String(value).replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength - 1)}…`;
}

export function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function formatNumber(value) {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("en-US").format(Number(value));
}

export function formatBytes(value) {
  if (value === null || value === undefined) return "—";

  const bytes = Number(value);
  if (!Number.isFinite(bytes)) return "—";

  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;

  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

export function renderMarkdown(value) {
  if (!value) return "";

  const normalized = String(value).trim();
  if (!normalized) return "";

  return markdown.render(normalized);
}

function toTelegramInternalChatId(chatId) {
  if (chatId === null || chatId === undefined) return null;

  const asString = String(chatId);
  if (!asString.startsWith("-100")) {
    return null;
  }

  return asString.slice(4);
}

export function rewriteTelegramMessageLinks(html, { chatId, view }) {
  if (!html) return "";

  const internalChatId = toTelegramInternalChatId(chatId);
  if (!internalChatId) {
    return html;
  }

  return html.replace(/href=("|')([^"']+)(\1)/g, (full, quote, href) => {
    let parsed;
    try {
      parsed = new URL(href);
    } catch {
      return full;
    }

    const host = parsed.hostname.toLowerCase();
    if (host !== "t.me" && host !== "telegram.me") {
      return full;
    }

    const parts = parsed.pathname.split("/").filter(Boolean);
    if (parts.length < 3 || parts[0] !== "c") {
      return full;
    }

    const linkedInternalChatId = parts[1];
    const linkedMessageId = Number.parseInt(parts[2], 10);
    if (!Number.isFinite(linkedMessageId)) {
      return full;
    }

    if (linkedInternalChatId !== internalChatId) {
      return full;
    }

    const localPath = `/messages/${chatId}/${linkedMessageId}`;
    const localHref = view ? `${localPath}?view=${encodeURIComponent(view)}` : localPath;
    return `href=${quote}${localHref}${quote}`;
  });
}

function parseCoordinate(value) {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).trim();
  if (!normalized) {
    return null;
  }

  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) {
    return null;
  }

  return numeric;
}

export function buildOpenStreetMapEmbedUrl(latitude, longitude, span = 0.08) {
  const lat = parseCoordinate(latitude);
  const lon = parseCoordinate(longitude);
  if (lat === null || lon === null) {
    return null;
  }

  const delta = Math.max(0.01, Number(span) || 0.08);
  const left = lon - delta;
  const right = lon + delta;
  const top = lat + delta;
  const bottom = lat - delta;

  const bbox = `${left.toFixed(6)},${bottom.toFixed(6)},${right.toFixed(6)},${top.toFixed(6)}`;
  const marker = `${lat.toFixed(6)},${lon.toFixed(6)}`;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${encodeURIComponent(marker)}`;
}

export function buildOpenStreetMapLink(latitude, longitude, zoom = 12) {
  const lat = parseCoordinate(latitude);
  const lon = parseCoordinate(longitude);
  if (lat === null || lon === null) {
    return null;
  }

  return `https://www.openstreetmap.org/?mlat=${encodeURIComponent(lat.toFixed(6))}&mlon=${encodeURIComponent(lon.toFixed(6))}#map=${encodeURIComponent(String(zoom))}/${encodeURIComponent(lat.toFixed(6))}/${encodeURIComponent(lon.toFixed(6))}`;
}

function normalizeBounds(value) {
  if (!value) return null;
  let parsed = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }

  const ne = parsed?.northeast || parsed?.ne;
  const sw = parsed?.southwest || parsed?.sw;
  const north = Number(ne?.lat);
  const east = Number(ne?.lng ?? ne?.lon);
  const south = Number(sw?.lat);
  const west = Number(sw?.lng ?? sw?.lon);

  if (![north, east, south, west].every(Number.isFinite)) {
    return null;
  }

  return { north, east, south, west };
}

export function buildLocationMarkersJson(locationItems) {
  const source = Array.isArray(locationItems) ? locationItems : [];

  return JSON.stringify(
    source.map((location, index) => ({
      lat: Number(location?.latitude),
      lon: Number(location?.longitude),
      label: location?.resolved_name || location?.mention || `Mapped location ${index + 1}`,
      link: location?.mapLink || null,
      precision: location?.precision_level || null,
      locationType: location?.location_type || null,
      bounds: normalizeBounds(location?.bounding_box_json),
    })),
  );
}

export function prettyJson(payload) {
  if (payload === null || payload === undefined) return "";

  if (typeof payload === "string") {
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
      return payload;
    }
  }

  return JSON.stringify(payload, null, 2);
}
